import React, { useEffect } from 'react';
import type { ReactNode } from 'react';
import classNames from 'classnames';
import { IS_DEVELOPMENT_BUILD } from '@jwp/ott-common/src/utils/common';
import type { Menu } from 'packages/common/types/config';
import { useTranslation } from 'react-i18next';

import styles from './Footer.module.scss';
import { useTheme } from '../../../../../ThemeContext';
import { useAppInfo } from '@jwp/ott-hooks-react/src/useAppInfo';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { Link } from 'react-router-dom';
import { RELATIVE_PATH_USER_TERMS_OF_USE, RELATIVE_PATH_USER_PRIVACY_POLICY } from '@jwp/ott-common/src/paths';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';

type Props = {
  text: ReactNode | string;
  logoSrc: any;
  menu: Menu[];
};

const Footer: React.FC<Props> = ({ text }) => {
  const { t } = useTranslation('common');
  const { universalStyles } = useTheme();

  const { canShowTermsOfUse, canShowPrivacyPolicy, canShowAboutUs, privacyPolicyPlacement, termsOfUsePlacement, aboutUsPlacement, helpPlacement } =
    useAppConfig();
  const { redirectionPolicyURL, redirectionTosURL, redirectionAboutUsURL, redirectionSupportURL } = useUIControls();
  const { clientSite, termsOfUse, privacyPolicy, aboutUs, supportEmail, supportEmailType } = useAppInfo();

  const shouldShowTermsOfUseLink = canShowTermsOfUse && termsOfUsePlacement?.includes('footer') && (termsOfUse?.en || termsOfUse?.es);
  const shouldShowPrivacyPolicyLink = canShowPrivacyPolicy && privacyPolicyPlacement?.includes('footer') && (privacyPolicy?.en || privacyPolicy?.es);
  const shouldShowAboutUsLink = aboutUsPlacement?.includes('footer');
  const shouldShowHelpLink = helpPlacement?.includes('footer') && supportEmail;

  const footerLinks = [
    {
      shouldShow: shouldShowPrivacyPolicyLink,
      url: redirectionPolicyURL,
      routepath: RELATIVE_PATH_USER_PRIVACY_POLICY,
      label: t('footer.privacy_policy'),
    },
    {
      shouldShow: shouldShowTermsOfUseLink,
      url: redirectionTosURL,
      routepath: RELATIVE_PATH_USER_TERMS_OF_USE,
      label: t('footer.terms_of_use'),
    },
    {
      shouldShow: shouldShowAboutUsLink,
      url: redirectionAboutUsURL,
      routepath: '/',
      label: t('footer.about_us'),
    },
    {
      shouldShow: shouldShowHelpLink,
      url: supportEmail,
      routepath: '/',
      label: t('footer.help'),
    },
    {
      shouldShow: Boolean(redirectionSupportURL),
      url: redirectionSupportURL,
      routepath: '/',
      label: t('footer.support'),
    },
  ];

  return (
    // The extra style below is just to fix the footer on mobile when the dev selector is shown
    <footer className={classNames(styles.footer, { [styles.testFixMargin]: IS_DEVELOPMENT_BUILD })}>
      <div className={styles.footerContainer}>
        <hr style={universalStyles.divider} />

        <div className={styles.footerInfoContaier}>
          <div className={styles.footerPoweredbyPart}>
            <span style={universalStyles.footerText}>{t('footer.powered_by')}</span>
            <img src="/images/tringappslogo.png" />
          </div>
          <div className={styles.clientSite}>
            <a href={`https://${clientSite}`} target="_blank" rel="noreferrer">
              {clientSite}
            </a>
          </div>
          <div className={styles.rightColInfo}>
            <div className={styles.footerLegalDocsPart}>
              {footerLinks
                .filter((link) => link.shouldShow)
                .map((link, index, arr) => (
                  <React.Fragment key={index}>
                    {link.label === t('footer.help') && supportEmail ? (
                      supportEmailType === 'email' ? (
                        <a href={`mailto:${supportEmail}`} className={styles.helpSupport} style={universalStyles.helpValueText}>
                          {link.label}
                        </a>
                      ) : supportEmailType === 'web' ? (
                        <a href={supportEmail} target="_blank" rel="noopener noreferrer" className={styles.helpSupport}>
                          {link.label}
                        </a>
                      ) : (
                        <span className={styles.helpSupport} style={universalStyles.helpValueText}>
                          {supportEmail}
                        </span>
                      )
                    ) : link.url ? (
                      <a href={link.url} target="_blank" rel="noreferrer">
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.routepath}>{link.label}</Link>
                    )}
                    {index < arr.length - 1 && <div className={styles.separater}></div>}
                  </React.Fragment>
                ))}
            </div>
            <div className={styles.separater}></div>
            <div style={universalStyles.footerText}>{text}</div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
